import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Share2, Download, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SmartNumberInput from '@/components/ui/SmartNumberInput';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import logoMain from '@/assets/logo-main.png';

const TariffCalculator = () => {
    const [propertyPrice, setPropertyPrice] = useState(250000);
    const [feePercentage, setFeePercentage] = useState(1); // Bloque 10.3: Porcentaje editable
    const [transferTax, setTransferTax] = useState(0);
    const [taxableExcess, setTaxableExcess] = useState(0);
    const [notaryBase, setNotaryBase] = useState(0);
    const [notaryIva, setNotaryIva] = useState(0);
    const [notaryTotal, setNotaryTotal] = useState(0);
    const [cnrRights, setCnrRights] = useState(0);
    const [totalFees, setTotalFees] = useState(0);

    const taxExemptionLimit = 28571.43;
    const cnrFixedFee = 2.86;

    // Helper eliminado, ahora usando SmartNumberInput directo




    useEffect(() => {
        const price = Number(propertyPrice) || 0;
        let tax = 0, excess = 0;
        if (price > taxExemptionLimit) {
            excess = price - taxExemptionLimit;
            tax = excess * 0.03;
        }
        setTaxableExcess(excess);
        setTransferTax(tax);

        const currentFee = Number(feePercentage) || 0;
        const base = price * (currentFee / 100);
        const iva = base * 0.13;
        const total = base + iva;
        setNotaryBase(base);
        setNotaryIva(iva);
        setNotaryTotal(total);

        const rights = (price * 0.0063) + cnrFixedFee;
        setCnrRights(rights);

        setTotalFees(tax + total + rights);
    }, [propertyPrice]);

    const formatCurrency = (value) => value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    const handleShare = () => { /* ... */ };
    const generatePDF = () => {
        try {
            const doc = new jsPDF();

            // Header Branding
            const img = new Image();
            img.src = logoMain;
            try {
                doc.addImage(img, 'PNG', 14, 10, 32, 12);
            } catch (e) {
                console.warn("Could not add logo", e);
            }

            doc.setFontSize(18);
            doc.setTextColor(26, 61, 92);
            doc.text("Servicios Futura - Desglose de Aranceles", 14, 32);

            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Fecha: ${new Date().toLocaleDateString()}`, 14, 40);
            doc.text(`Valor de Propiedad: ${formatCurrency(propertyPrice)}`, 14, 46);

            const tableData = [
                ["Impuesto de Transferencia (3%)", formatCurrency(transferTax)],
                ["Honorarios Legales + IVA", formatCurrency(notaryTotal)],
                ["Derechos de Registro (CNR)", formatCurrency(cnrRights)],
                ["TOTAL ESTIMADO", formatCurrency(totalFees)]
            ];

            doc.autoTable({
                startY: 55,
                head: [['Concepto', 'Monto Estimado']],
                body: tableData,
                theme: 'grid',
                headStyles: { fillColor: [26, 61, 92] },
                columnStyles: {
                    0: { cellWidth: 120 },
                    1: { cellWidth: 40, halign: 'right', fontStyle: 'bold' }
                }
            });

            // Footer Branding
            const pageHeight = doc.internal.pageSize.height;
            doc.setFontSize(9);
            doc.setTextColor(120);
            doc.text('Servicios Futura | El Salvador', 14, pageHeight - 15);
            doc.text('info@serviciosfutura.com', 14, pageHeight - 10);
            doc.setFontSize(8);
            doc.setTextColor(160);
            doc.text('© 2026 Servicios Futura', 14, pageHeight - 5);

            doc.save("aranceles-futura.pdf");
        } catch (error) {
            console.error("Error generating PDF:", error);
            alert("Hubo un error al generar el PDF. Por favor intenta de nuevo.");
        }
    };

    const InfoCard = ({ title, value, formula, explanation, details, icon }) => (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-6 rounded-lg shadow-md border">
            <div className="flex justify-between items-start mb-4">
                <h4 className="text-xl font-semibold text-secondary pr-4">{title}</h4>
                <span className="font-bold text-2xl text-primary whitespace-nowrap">{formatCurrency(value)}</span>
            </div>
            <div className="space-y-4 text-sm text-gray-600">
                <div className="p-3 bg-gray-50 rounded-md">
                    <p><span className="font-semibold text-gray-800">Fórmula:</span> {formula}</p>
                    {details && <div className="mt-2 pl-4 border-l-2 border-primary/50 text-gray-500">{details}</div>}
                </div>
                <p className="pt-2 flex gap-3"><Info size={22} className="text-gray-400 flex-shrink-0 mt-0.5" /><span>{explanation}</span></p>
            </div>
        </motion.div>
    );

    return (
        <div className="bg-transparent rounded-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {/* INPUTS & TOTAL COLUMN */}
                <div className="space-y-8">
                    <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                        <h3 className="text-xl font-semibold text-secondary mb-4">Valor de la Propiedad</h3>
                        <div className="relative">
                            <DollarSign size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                            <SmartNumberInput
                                value={propertyPrice}
                                onChange={setPropertyPrice}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-lg"
                                placeholder="250000"
                            />
                        </div>
                        <p className="text-sm text-gray-500 mt-3">Ingresa el valor de la propiedad para calcular los aranceles de compra.</p>

                        <div className="mt-6">
                            <h3 className="text-xl font-semibold text-secondary mb-4">Honorarios Legales (%)</h3>
                            <div className="relative">
                                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">%</span>
                                <SmartNumberInput
                                    value={feePercentage}
                                    onChange={setFeePercentage}
                                    className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-lg"
                                    placeholder="1"
                                />
                            </div>
                            <p className="text-sm text-gray-500 mt-3">Porcentaje de honorarios sobre el valor de la propiedad (Default: 1%).</p>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ delay: 0.1 }}
                        className="bg-secondary text-white rounded-xl p-8 text-center shadow-xl"
                    >
                        <div className="text-sm mb-1 text-white/90 uppercase tracking-wider">Total de Aranceles Estimados</div>
                        <div className="text-5xl font-bold">{formatCurrency(totalFees)}</div>
                    </motion.div>

                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex flex-col gap-4 pt-8 border-t">
                        <Button size="lg" onClick={generatePDF} variant="outline" className="w-full border-secondary text-secondary hover:bg-secondary hover:text-white shadow-sm"><Download size={18} className="mr-2" />Descargar Desglose PDF</Button>
                        <Button size="lg" onClick={handleShare} className="w-full bg-green-500 hover:bg-green-600 text-white shadow-sm"><Share2 size={18} className="mr-2" />Compartir por WhatsApp</Button>
                    </motion.div>
                </div>

                {/* RESULTS COLUMN */}
                <div className="space-y-8">
                    <InfoCard
                        title="Impuesto de Transferencia"
                        value={transferTax}
                        formula={`(Precio - ${formatCurrency(taxExemptionLimit)}) × 3%`}
                        details={<p>Excedente: {formatCurrency(taxableExcess)} × 3% = <span className="font-semibold text-gray-800">{formatCurrency(transferTax)}</span></p>}
                        explanation="Este impuesto lo cobra el Estado sobre el monto que excede la base exenta de $28,571.43. Se paga una sola vez al momento de la compra."
                    />
                    <InfoCard
                        title="Honorarios Legales"
                        value={notaryTotal}
                        formula={`Precio × ${feePercentage || 0}% + IVA (13%)`}
                        details={
                            <>
                                <p>Base ({feePercentage || 0}%): <span className="font-semibold text-gray-800">{formatCurrency(notaryBase)}</span></p>
                                <p>IVA (13%): <span className="font-semibold text-gray-800">{formatCurrency(notaryIva)}</span></p>
                            </>
                        }
                        explanation="Cubre la elaboración de la escritura, revisión de documentos y proceso legal. El 13% de IVA es un requisito de ley para servicios profesionales."
                    />
                    <InfoCard
                        title="Derechos de Inscripción (CNR)"
                        value={cnrRights}
                        formula={`(Precio × 0.63%) + $2.86`}
                        details={<p>{`( ${formatCurrency(propertyPrice)} x 0.63% ) + ${formatCurrency(cnrFixedFee)}`} = <span className="font-semibold text-gray-800">{formatCurrency(cnrRights)}</span></p>}
                        explanation="Tasa que cobra el Centro Nacional de Registros (CNR) para inscribir legalmente la propiedad a nombre del nuevo comprador."
                    />
                </div>
            </div>
            <div className="mt-12 text-xs text-gray-500 text-center max-w-2xl mx-auto">
                *Los valores mostrados son aproximados y se basan en las leyes vigentes en El Salvador. Pueden existir otros gastos menores no incluidos aquí.
            </div>
        </div>
    );
};

export default TariffCalculator;
